import { get, last, uniq, uniqBy } from 'lodash';

import { useMemo } from 'react';

import { getNodes, sortingToGql, useExplorationDataQuery } from '@/graphql';
import { useInitialQueryLoadCondition } from '@/lib/hooks';
import { flattenNestedList, countLeaves } from '@/explore/grouping';
import {
  Pipeline,
  Field,
  QueryVariables,
  Visualisation,
  isAggregatedVisualisation,
} from '@/explore/types';
import { filterVariablesForPipeline } from '@/explore/utils';
import { useMetadataContext } from '@/explore/metadata-context';
import { getPipelineFields, precisionToTimeInterval } from '@/explore/pipeline/state';
import { useExplorationCellContext } from '@/explore/exploration/exploration-cell-context';
import { sortDateGroupingsLast } from '@/explore/utils/grouping';
import { useAccountContext, useAccountTimezone } from '@/lib/accounts/context';
import { useExplorationContext } from '@/explore/exploration/exploration-context';
import { buildSeriesMenuItems } from '@/explore/edit-visualisation/utils';
import { QueryError } from '@/explore/components/error';
import { useMemoizeDataQueryVariables } from '@/explore/utils/use-memoize-data-query-variables';
import { useRefetchCellData } from '@/explore/exploration/hooks';
import { useContextMenu } from '@/components/context-menu/context-menu-context';
import { Icon } from '@/components/icon';
import { createPipeline } from '@/core/pipeline';
import { isCellWithTitle } from '@/core/cell';
import {
  buildFilterFromGroupFilter,
  drillDownFromAggregatingOperation,
  GroupFilter,
} from '@/explore/utils/drilldown';
import { formatPropertyValue } from '@/explore/utils/format';
import { DropdownIconSizePx, DropdownMenuItem } from '@/components/dropdown';
import { Loader } from '@/components/loader';
import {
  getVisualisationOrientation,
  getVisualisationOptions,
  getVisualisationShowTotals,
  isVisualisationStacked,
} from '@/core/visualisation';

import {
  disableShowValues,
  enableShowValues,
  isBigNumberWithComparison,
  resetSeriesOptions,
  setSecondaryAxisKey,
  setSeriesChartType,
  setSeriesColor,
} from '../utils';
import { GroupedTimeSeriesChart } from '../../charts/timeseries-chart/grouped-timeseries-chart';
import { detectChartInterval } from '../../charts/timeseries-chart/utils';
import { DataWarning, AggregationWarning } from '../warning';
import { getChartType, getQueryForVisualisation } from '../utils';
import { Histogram } from '../../charts/histogram';
import { VisualisationLoader } from '..';
import { usePeriodToDateQuery } from './use-period-to-date-query';
import {
  explodeCombinedKey,
  generateGroupedCategoryData,
  generateGroupedCategoryDataVertical,
  generateGroupedTimeSeriesData,
  generateHistogramData,
  getSeries,
  isCombinedKey,
} from '../../charts/grouped-chart/utils';
import { BigNumber } from '../../charts/big-number';
import { GroupedHorizontalBarChart } from '../../charts/horizontal-bar-chart/grouped-horizontal-chart';

import { SeriesMetaData } from '../../charts/grouped-chart/types';

import { GroupedCategoryChart } from '../../charts/category-chart/grouped-category-chart';

import styles from './visualisation-graph.module.scss';

interface VisualisationGraphProps {
  idx: number;
  visualisation: Visualisation;
  pipeline: Pipeline;
  variables: QueryVariables;
  fields: Field[];
  onVisualisationChange: (visualisation: Visualisation) => void;
  isInView: boolean;
  isResized: boolean;
  showingOptions?: boolean;
}

export const VisualisationGraph = (props: VisualisationGraphProps) => {
  const { visualisation, fields, variables, isResized, onVisualisationChange, showingOptions } =
    props;
  const visualisationOptions = getVisualisationOptions(visualisation);
  const { account, isFeatureEnabled } = useAccountContext();
  const { exploration, drilldownFromPipeline } = useExplorationContext();

  const chartType = getChartType(visualisation, fields);
  const {
    visualisationPipeline,
    queryPipeline,
    sort,
    limit,
    groups: queryGroups,
  } = getQueryForVisualisation({ pipeline: props.pipeline, fields, visualisation, exploration });

  const timezone = useAccountTimezone();
  const { models, metrics } = useMetadataContext();
  const { cell, isCollapsed, isTableVisible } = useExplorationCellContext();
  const [onCompleted, onError, skipInitialLoad] = useInitialQueryLoadCondition(props.isInView);
  const { openContextMenu } = useContextMenu();

  const pipelineVariables = filterVariablesForPipeline(queryPipeline, props.variables);

  const skip = skipInitialLoad || isCollapsed;
  const { data, error, refetch, loading } = useExplorationDataQuery({
    variables: useMemoizeDataQueryVariables({
      baseModelId: queryPipeline.baseModelId,
      accountId: account.accountId,
      pipeline: queryPipeline.operations,
      sort: sortingToGql(sort),
      // Fetch one extra record to check if there's more data
      first: limit !== undefined ? limit + 1 : limit,
      variables: pipelineVariables,
      options: {
        labels: { exploration: exploration.explorationId, block: cell.id },
        optimize: isFeatureEnabled('optimizeQuery'),
      },
    }),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    skip,
    onCompleted,
    onError,
  });
  useRefetchCellData(refetch);

  const allRecords = getNodes(data?.account?.query);
  const hasNextPage = countLeaves(allRecords) > (limit ?? 0);
  // Remove the extra item we fetched
  const records = allRecords.slice(0, limit);
  const loaded = data?.account?.query !== undefined;

  const isBigNumberOnly = chartType === 'single-value';
  const visRecords = queryGroups.length > 1 ? records : flattenNestedList(records);
  const bigNumberFieldKey = visualisation.viewOptions?.bigNumber?.key;
  const axesOptions = visualisation.viewOptions?.axes;
  const isBigNumberShown = bigNumberFieldKey !== undefined;
  const isNotFullData = hasNextPage && chartType !== 'single-value';
  const visualisationFields = getPipelineFields(visualisationPipeline, {
    models,
    variables,
    metrics,
    exploration,
  });

  const mainAxisKey = visualisation.mainAxisKey;
  const mainAxisField =
    mainAxisKey === undefined
      ? undefined
      : visualisationFields.find((field) => field.key === mainAxisKey);
  const isAggregated = isAggregatedVisualisation(visualisation);
  const requiresAggregation = useMemo(() => {
    if (queryGroups.length > 0 || mainAxisKey === undefined) {
      return false;
    }
    const mainAxisValues = flattenNestedList(records).map((item) => item[mainAxisKey]);
    return mainAxisValues.length !== uniq(mainAxisValues).length;
  }, [records, mainAxisKey, queryGroups.length]);

  // Period-to-date query for BigNumber comparisons
  const { isPeriodToDateLoading, periodToDateError, periodToDateFlattenedRecords } =
    usePeriodToDateQuery({
      pipeline: props.pipeline,
      fields,
      visualisation,
      exploration,
      variables,
      mainAxisField,
      skip,
    });

  if (error !== undefined) {
    return <QueryError error={error} refetch={refetch} />;
  }

  const buildSeriesMenu = (allSeries: SeriesMetaData[], seriesKey: string) => {
    const series = allSeries.find((s) => s.key === seriesKey);

    if (series === undefined) {
      return [];
    }

    return buildSeriesMenuItems({
      color: series.color,
      chartTypes: [series.chartType],
      axes: [series.axis],
      showValues: [series.showValues],
      isChartTypeDisabled: false,
      isSecondaryAxisDisabled: false,
      onShowValuesChange: (showValues) => {
        onVisualisationChange({
          ...visualisation,
          viewOptions: showValues
            ? enableShowValues(seriesKey, visualisation.viewOptions)
            : disableShowValues(seriesKey, visualisation.viewOptions),
        });
      },
      onColorChange: (color) =>
        onVisualisationChange({
          ...visualisation,
          viewOptions: setSeriesColor(seriesKey, color, visualisation.viewOptions),
        }),
      onChartTypeChange: (chartType) =>
        onVisualisationChange({
          ...visualisation,
          viewOptions: setSeriesChartType(seriesKey, chartType, visualisation.viewOptions),
        }),
      onSecondaryAxisChange: (checked) => {
        onVisualisationChange({
          ...visualisation,
          viewOptions: setSecondaryAxisKey(seriesKey, checked, visualisation.viewOptions),
        });
      },
      onReset: () => {
        onVisualisationChange({
          ...visualisation,
          viewOptions: resetSeriesOptions(seriesKey, visualisation.viewOptions),
        });
      },
    });
  };

  const handleDrilldown = (input: { key: string; groupFilters: GroupFilter[] }) => {
    const { key, groupFilters } = input;

    const { pipeline, sort } = drillDownFromAggregatingOperation({
      key,
      pipeline: visualisationPipeline,
      filterOperations: groupFilters.map((groupFilter) => {
        const field = visualisationFields.find((field) => field.key === groupFilter.key);
        if (field === undefined) {
          throw new Error(`Field ${groupFilter.key} not found`);
        }
        return buildFilterFromGroupFilter({
          groupFilter,
          field,
          timezone,
        });
      }),
      exploration,
    });

    drilldownFromPipeline({
      pipeline,
      title: isCellWithTitle(cell) ? (cell.title ?? undefined) : undefined,
      afterCellId: cell.id,
      sort,
    });
  };

  const buildTimeseriesMenu = (input: {
    key: string;
    groupFilters: GroupFilter[];
    allSeries: SeriesMetaData[];
  }): DropdownMenuItem[] => {
    const { key, groupFilters, allSeries } = input;

    const lastGroupFilter = last(groupFilters);
    if (lastGroupFilter === undefined) {
      throw new Error('Group filter is undefined');
    }

    const lastGroupFilterField = visualisationFields.find(
      (field) => field.key === lastGroupFilter.key,
    );
    if (lastGroupFilterField === undefined) {
      throw new Error(`Field ${lastGroupFilter.key} not found`);
    }

    const formattedValue = formatPropertyValue(lastGroupFilter.value, {
      precision: lastGroupFilterField.precision,
      type: lastGroupFilterField.type,
      format: lastGroupFilterField.format,
      timezone,
    });

    const hasCombinedSeries = allSeries.some((series) => isCombinedKey(series.key));
    if (hasCombinedSeries) {
      return [
        { type: 'header', label: 'Drilldown by' },
        {
          label: `${lastGroupFilterField.name} is ${formattedValue}`,
          onClick: () => handleDrilldown({ key, groupFilters }),
        },
        {
          type: 'divider',
        },
        { type: 'header' as const, label: 'Filter drilldown' },
        ...uniqBy(allSeries, (series) => series.key.split(':').slice(0, -1).join(':')).map(
          (series) => {
            const { groupKey, groupValue } = explodeCombinedKey(series.key);
            const groupField = visualisationFields.find((field) => field.key === groupKey);
            if (groupField === undefined) {
              throw new Error(`Field ${groupKey} not found`);
            }
            return {
              label: `${groupField.name} is ${groupValue}`,
              onClick: () =>
                handleDrilldown({
                  key,
                  groupFilters: [
                    ...groupFilters,
                    {
                      key: groupKey,
                      value: groupValue,
                    },
                  ],
                }),
            };
          },
        ),
      ];
    }

    return [
      {
        label: `Show drilldown for ${formattedValue}`,
        icon: <Icon name="ExternalLink" size={DropdownIconSizePx} />,
        onClick: () => handleDrilldown({ key, groupFilters }),
      },
    ];
  };

  const renderBigNumber = (
    { loading, isTimeSeries }: { loading?: boolean; isTimeSeries?: boolean } = {
      loading: false,
      isTimeSeries: false,
    },
  ) => {
    const bigNumberField = isBigNumberShown
      ? visualisationFields.find((field) => field.key === bigNumberFieldKey)
      : undefined;

    if (bigNumberField === undefined) {
      return null;
    }

    if (isBigNumberWithComparison(visualisation.viewOptions?.bigNumber)) {
      const bigNumberComparisonKey = visualisation.viewOptions?.bigNumber?.comparisonKey;
      const bigNumberComparisonField =
        bigNumberComparisonKey !== undefined
          ? visualisationFields.find((field) => field.key === bigNumberComparisonKey)
          : undefined;

      return (
        <>
          {isNotFullData ? <DataWarning /> : null}
          <BigNumber
            field={bigNumberField}
            comparisonField={bigNumberComparisonField}
            direction={visualisation.viewOptions?.bigNumber?.comparisonDirection}
            showLabel={visualisation.viewOptions?.bigNumber?.showComparisonLabel}
            comparisonType={visualisation.viewOptions?.bigNumber?.comparisonType}
            data={visRecords}
            loading={loading}
            hasHeader={showingOptions}
            isTimeSeries={isTimeSeries}
            mainAxisField={mainAxisField}
            comparisonMode={visualisation.viewOptions.bigNumber.comparisonMode}
            periodToDateData={periodToDateFlattenedRecords}
            isPeriodToDateLoading={isPeriodToDateLoading}
            isPeriodToDateError={periodToDateError !== undefined}
            timezone={timezone}
          />
        </>
      );
    }

    return (
      <BigNumber
        field={bigNumberField}
        hasHeader={showingOptions}
        isTimeSeries={isTimeSeries}
        data={visRecords}
        loading={loading}
        timezone={timezone}
      />
    );
  };

  if (!loaded) {
    if (isBigNumberOnly) {
      return renderBigNumber({ loading: true });
    }
    return <VisualisationLoader isResized={!isTableVisible && isResized} />;
  }

  const renderHistogram = () => {
    const field = fields.find(
      ({ key }) => key === get(last(queryPipeline.operations)?.parameters, 'key'),
    );

    if (field === undefined) {
      throw new Error('Visualisation field not found');
    }

    const data = generateHistogramData(records, visualisation);

    return (
      <Histogram
        data={data}
        categoryLabel={field.name}
        field={field}
        isResized={isResized}
        onBucketClick={({ x, y, filters }) => {
          openContextMenu({
            position: {
              x: x - 10,
              y: y + 10,
            },
            items: [
              {
                label: 'Show drilldown',
                icon: <Icon name="ExternalLink" size={DropdownIconSizePx} />,
                onClick: () => {
                  drilldownFromPipeline({
                    pipeline: createPipeline({
                      ...props.pipeline,
                      operations: [...props.pipeline.operations, ...filters],
                    }),
                    title: isCellWithTitle(cell) ? (cell.title ?? undefined) : undefined,
                    afterCellId: cell.id,
                  });
                },
              },
            ],
          });
        }}
        buildSeriesMenu={(seriesKey) => buildSeriesMenu(data.series, seriesKey)}
      />
    );
  };

  const renderTimeSeriesChart = () => {
    const data = generateGroupedTimeSeriesData(
      records,
      visualisation,
      queryGroups,
      visualisationFields,
      axesOptions,
    );

    const precision = isAggregated
      ? (precisionToTimeInterval(
          sortDateGroupingsLast(visualisation.aggregation.groups).at(-1)?.precision,
          variables,
        ) ?? 'month')
      : (precisionToTimeInterval(mainAxisField?.precision, variables) ?? detectChartInterval(data));

    const isStacked = isVisualisationStacked(visualisationOptions, getSeries(data).length > 1);
    return (
      <>
        {requiresAggregation ? <AggregationWarning /> : isNotFullData ? <DataWarning /> : null}
        {isBigNumberShown ? (
          <div className={styles.chartOverlay}>{renderBigNumber({ isTimeSeries: true })}</div>
        ) : null}
        <GroupedTimeSeriesChart
          data={data}
          grouping={queryGroups}
          aggPeriod={precision}
          stacked={isStacked}
          showTotals={getVisualisationShowTotals(visualisationOptions)}
          onTimeClick={({ x, y, key, groupFilters }) => {
            openContextMenu({
              position: {
                x: x - 10,
                y: y + 10,
              },
              items: buildTimeseriesMenu({ key, groupFilters, allSeries: getSeries(data) }),
            });
          }}
          hideGrid={false}
          timezone={timezone}
          isResized={props.isResized}
          isTableVisible={isTableVisible}
          buildSeriesMenu={(seriesKey) => buildSeriesMenu(getSeries(data), seriesKey)}
        />
      </>
    );
  };

  const renderCategoryChart = () =>
    getVisualisationOrientation(visualisation, fields) === 'horizontal'
      ? renderHorizontalBarChart()
      : renderVerticalCategoryChart();

  const renderVerticalCategoryChart = () => {
    const data = generateGroupedCategoryDataVertical(
      records,
      visualisation,
      queryGroups,
      visualisationFields,
      axesOptions,
    );

    const isStacked = isVisualisationStacked(visualisationOptions, getSeries(data).length > 1);
    return (
      <>
        {isBigNumberShown ? <div className={styles.chartOverlay}>{renderBigNumber()}</div> : null}
        <GroupedCategoryChart
          data={data}
          grouping={queryGroups}
          stacked={isStacked}
          showTotals={getVisualisationShowTotals(visualisationOptions)}
          onCategoryClick={({ x, y, key, groupFilters }) => {
            openContextMenu({
              position: {
                x: x - 10,
                y: y + 10,
              },
              items: buildTimeseriesMenu({ key, groupFilters, allSeries: getSeries(data) }),
            });
          }}
          hideGrid={false}
          timezone={timezone}
          isResized={props.isResized}
          isTableVisible={isTableVisible}
          buildSeriesMenu={(seriesKey) => buildSeriesMenu(getSeries(data), seriesKey)}
        />
        {requiresAggregation ? <AggregationWarning /> : isNotFullData ? <DataWarning /> : null}
      </>
    );
  };

  const renderHorizontalBarChart = () => {
    const data = generateGroupedCategoryData(
      records,
      visualisation,
      queryGroups,
      visualisationFields,
    );

    const isStacked = isVisualisationStacked(visualisationOptions, getSeries(data).length > 1);
    return (
      <>
        <GroupedHorizontalBarChart
          data={data}
          grouping={queryGroups}
          stacked={isStacked}
          showTotals={getVisualisationShowTotals(visualisationOptions)}
          onCategoryClick={({ x, y, key, groupFilters = [] }) => {
            openContextMenu({
              position: {
                x: x - 10,
                y: y + 10,
              },
              items: [
                {
                  label: 'Show drilldown',
                  icon: <Icon name="ExternalLink" size={DropdownIconSizePx} />,
                  onClick: () => handleDrilldown({ key, groupFilters }),
                },
              ],
            });
          }}
          isResized={props.isResized}
          isTableVisible={isTableVisible}
          isNotFullData={isNotFullData}
          buildSeriesMenu={(seriesKey) => buildSeriesMenu(getSeries(data), seriesKey)}
        />
        {requiresAggregation ? <AggregationWarning /> : isNotFullData ? <DataWarning /> : null}
      </>
    );
  };

  return (
    <div className={styles.wrapper}>
      {loading && (
        <div className={styles.loaderOverlay}>
          <Loader />
        </div>
      )}
      {chartType === 'single-value'
        ? renderBigNumber()
        : chartType === 'grouped-timeseries'
          ? renderTimeSeriesChart()
          : chartType === 'bar'
            ? renderCategoryChart()
            : chartType === 'histogram'
              ? renderHistogram()
              : null}
    </div>
  );
};
